import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { embedDashboard, type EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { environment } from '../../../environments/environment';
import { SupersetService } from '../../services/superset.service';

interface AdminDashboardDefinition {
  id: string;
  title: string;
  category: string;
  summary: string;
  accent: string;
  highlights: string[];
}

type DashboardOverviewView = 'card' | 'table';

const ADMIN_DASHBOARDS: AdminDashboardDefinition[] = [
  // Credit Request Table hidden while credit request flow is disabled
  // {
  //   id: environment.supersetDashboardId,
  //   title: 'Credit Request Table',
  //   category: 'Credits',
  //   summary: 'Track and manage all compute and data credit requests submitted by users and organisations across the platform.',
  //   accent: '#00d084',
  //   highlights: ['Credit requests', 'Compute usage', 'Organisation billing'],
  // },
  {
    id: environment.userActivitySupersetDashboardId,
    title: 'User Activity Table',
    category: 'Usage',
    summary: 'A focused view of user behaviour, activity patterns and engagement signals over time.',
    accent: '#00b371',
    highlights: ['Activity trends', 'User behaviour', 'Adoption signals'],
  },
  {
    id: environment.computeRoleSupersetDashboardId,
    title: 'Compute Role Table',
    category: 'Compute',
    summary: 'Monitor compute role assignments, usage comparisons, and contribution health across the platform.',
    accent: '#059669',
    highlights: ['Role assignments', 'Compute monitoring', 'Contribution health'],
  },
  {
    id: environment.organisationRequestsSupersetDashboardId,
    title: 'Organisation Request Table',
    category: 'Organisations',
    summary: 'Review and act on pending organisation requests, join requests, and approval workflows.',
    accent: '#047857',
    highlights: ['Org requests', 'Join approvals', 'Workflow status'],
  },
  {
    id: environment.datasetAccessRequestsSupersetDashboardId,
    title: 'ACL Table Dashboard',
    category: 'Access Control',
    summary: 'Inspect Access Control List configurations, review permissions, and validate dataset access policies.',
    accent: '#065f46',
    highlights: ['ACL policies', 'Dataset access', 'Permission review'],
  },
];

@Component({
  selector: 'app-admin-superset-dashboard',
  standalone: true,
  imports: [CommonModule, HeaderComponent, AdminSidebarComponent, PageHeaderComponent],
  templateUrl: './superset-dashboard.component.html',
  styleUrl: './superset-dashboard.component.scss',
})
export class AdminSupersetDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('supersetMount')
  set supersetMountRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.supersetMount = ref;
    if (ref) {
      void this.tryLoadSelectedDashboard();
    }
  }

  private supersetMount?: ElementRef<HTMLDivElement>;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private supersetService = inject(SupersetService);
  private ngZone = inject(NgZone);
  private routeSubscription?: Subscription;
  private viewReady = false;
  private embedRequestToken = 0;

  readonly dashboards = ADMIN_DASHBOARDS;
  readonly featuredDashboard = ADMIN_DASHBOARDS[0];
  selectedDashboard: AdminDashboardDefinition | null = null;
  requestedDashboardId: string | null = null;
  isLoading = false;
  errorMessage: string | null = null;
  overviewView: DashboardOverviewView = 'card';
  private embeddedDashboard: EmbeddedDashboard | null = null;

  ngOnInit(): void {
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const dashboardId = params.get('dashboardId')?.trim() ?? null;
      this.requestedDashboardId = dashboardId;
      this.selectedDashboard = dashboardId ? this.findDashboardById(dashboardId) : null;

      if (dashboardId && !this.selectedDashboard) {
        this.errorMessage = `No dashboard configuration was found for ${dashboardId}.`;
        this.isLoading = false;
        this.teardownEmbeddedDashboard();
        return;
      }

      this.errorMessage = null;
      this.isLoading = false;

      if (!this.selectedDashboard) {
        this.teardownEmbeddedDashboard();
        return;
      }

      void this.tryLoadSelectedDashboard();
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    void this.tryLoadSelectedDashboard();
  }

  get otherDashboards(): AdminDashboardDefinition[] {
    if (!this.selectedDashboard) {
      return this.dashboards;
    }

    return this.dashboards.filter((dashboard) => dashboard.id !== this.selectedDashboard?.id);
  }

  openDashboard(dashboard: AdminDashboardDefinition): void {
    void this.router.navigate(['/admin/analytics-dashboard', dashboard.id]);
  }

  goToOverview(): void {
    void this.router.navigate(['/admin/analytics-dashboard']);
  }

  isDashboardActive(dashboardId: string): boolean {
    return this.selectedDashboard?.id === dashboardId;
  }

  getShortDashboardId(dashboardId: string): string {
    if (dashboardId.length <= 16) {
      return dashboardId;
    }

    return `${dashboardId.slice(0, 8)}...${dashboardId.slice(-4)}`;
  }

  trackByDashboardId(_: number, dashboard: AdminDashboardDefinition): string {
    return dashboard.id;
  }

  setOverviewView(view: DashboardOverviewView): void {
    this.overviewView = view;
  }

  private findDashboardById(dashboardId: string): AdminDashboardDefinition | null {
    return this.dashboards.find((dashboard) => dashboard.id === dashboardId) ?? null;
  }

  private async tryLoadSelectedDashboard(): Promise<void> {
    const supersetDomain = environment.supersetDashboardUrl?.trim();
    const guestTokenEndpoint = environment.supersetGuestTokenEndpoint?.trim();

    if (!this.viewReady || !this.selectedDashboard || !this.supersetMount?.nativeElement) {
      return;
    }

    const selectedDashboard = this.selectedDashboard;

    if (!supersetDomain || !guestTokenEndpoint) {
      this.errorMessage =
        'Superset SDK is not configured. Set supersetDashboardId, supersetDashboardUrl, and supersetGuestTokenEndpoint.';
      this.isLoading = false;
      return;
    }

    const currentRequestToken = ++this.embedRequestToken;
    this.errorMessage = null;
    this.isLoading = true;
    this.teardownEmbeddedDashboard();

    try {
      let guestTokenPromise: Promise<string> | null = this.supersetService.getGuestToken();
      const embedUuid = await this.supersetService.resolveEmbedUuid(selectedDashboard.id);

      if (currentRequestToken !== this.embedRequestToken) {
        return;
      }

      const embeddedDashboard = await embedDashboard({
        id: embedUuid,
        supersetDomain: supersetDomain.replace(/\/+$/, ''),
        mountPoint: this.supersetMount.nativeElement,
        fetchGuestToken: () => {
          const pending = guestTokenPromise ?? this.supersetService.getGuestToken();
          guestTokenPromise = null;
          return pending;
        },
        dashboardUiConfig: {
          hideTitle: true,
          urlParams: {
            standalone: '1',
            native_filters_key: '',
          },
        },
      });

      if (currentRequestToken !== this.embedRequestToken) {
        embeddedDashboard.unmount();
        return;
      }

      this.embeddedDashboard = embeddedDashboard;
    } catch (error) {
      console.error('Failed to embed Superset dashboard', error);
      if (currentRequestToken === this.embedRequestToken) {
        this.errorMessage = `Unable to load "${selectedDashboard.title}". Please verify guest token middleware route and dashboard access.`;
      }
    } finally {
      if (currentRequestToken === this.embedRequestToken) {
        // embedDashboard() resolves off an iframe 'load'/postMessage handshake, which can
        // settle outside Angular's zone and leave the UI showing a stale spinner until the
        // next unrelated event. Re-enter the zone explicitly so the view updates immediately.
        this.ngZone.run(() => {
          this.isLoading = false;
        });
      }
    }
  }

  private teardownEmbeddedDashboard(): void {
    this.embeddedDashboard?.unmount();
    this.embeddedDashboard = null;

    if (this.supersetMount?.nativeElement) {
      this.supersetMount.nativeElement.innerHTML = '';
    }
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.embedRequestToken += 1;
    this.teardownEmbeddedDashboard();
  }
}
